import React, { CSSProperties } from 'react';
import { Color } from 'lib/components/types';
import classes from './index.module.scss';

/**
 * Typography variant options
 * Defines the available text styles and sizes
 */
type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'subheading1' | 'subheading2' | 'body1' | 'body2';

/**
 * Mapping of variants to HTML elements
 */
type VariantMapping = Record<Variant, string>;

/**
 * Font weight options
 */
type Weight = 'light' | 'regular' | 'medium' | 'bold' | 'heavy';

/**
 * Mapping of typography variants to HTML elements
 * Defines which HTML tag should be used for each variant
 */
const variantsMapping: VariantMapping = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
	h6: 'h6',
	p: 'p',
	subheading1: 'h6',
	subheading2: 'h6',
	body1: 'p',
	body2: 'p',
};

/**
 * Props interface for the Typography component
 */
interface Props {
	/** Content to display */
	children: React.ReactNode;
	/** Text style variant */
	variant?: Variant;
	/** Font weight */
	weight?: Weight;
	/** Text color */
	color?: Color;
	/** Additional CSS class names */
	className?: string;
	/** Inline styles */
	style?: CSSProperties;
}

/**
 * Typography component
 * Renders text with consistent styling based on the design system
 * Supports different variants, weights, and colors
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Typography = ({ variant = 'p', color, weight, className = '', children, style }: Props) => {
	// Determine which HTML element to use based on the variant
	const cmpName = variant ? variantsMapping[variant] : 'p';

	// Build class names based on props
	const variantClassName = `typography-variant-${variant}`;
	const colorClassName = color ? `typography-color-${color}` : '';
	const weightClassName = weight ? `typography-weight-${weight}` : '';
	// Combine all classes
	const combinedClassNames = `${classes[variantClassName]} ${classes[colorClassName]} ${classes[weightClassName]} ${className}`;

	// Create the appropriate HTML element with the combined classes
	return React.createElement(cmpName, { className: combinedClassNames, style }, children);
};

export default Typography;
